/*
 * © 2024 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnalysisFileNameLengthLimitExceededError = exports.AnalysisTargetSizeLimitExceededError = exports.AnalysisResultSizeLimitExceededError = exports.AnalysisFileCountLimitExceededError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name AnalysisFileCountLimitExceededError
 * @description This error occurs when the analysis target has a supported file count that exceeds current system limits.
 *
 * To reduce the file count, use a `.snyk` file to ignore specified directories or files. Alternatively, use the Snyk CLI to analyze individual subdirectories separately.
 *
 * See more:
 * - [https://docs.snyk.io/scan-applications/supported-languages-and-frameworks/supported-languages-frameworks-and-feature-availability-overview#code-analysis-snyk-code](https://docs.snyk.io/scan-applications/supported-languages-and-frameworks/supported-languages-frameworks-and-feature-availability-overview#code-analysis-snyk-code)
 * - [https://docs.snyk.io/scan-applications/start-scanning-using-the-cli-web-ui-or-api/snyk-code-and-your-repositories/excluding-directories-and-files-from-the-import-process](https://docs.snyk.io/scan-applications/start-scanning-using-the-cli-web-ui-or-api/snyk-code-and-your-repositories/excluding-directories-and-files-from-the-import-process)
 * - [https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli](https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli)
 * @summary Analysis file count limit exceeded
 * @category Code
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class AnalysisFileCountLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Analysis file count limit exceeded',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-code-0001',
            status: 422,
            errorCode: 'SNYK-CODE-0001',
            level: 'error',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-applications/supported-languages-and-frameworks/supported-languages-frameworks-and-feature-availability-overview#code-analysis-snyk-code',
                'https://docs.snyk.io/scan-applications/start-scanning-using-the-cli-web-ui-or-api/snyk-code-and-your-repositories/excluding-directories-and-files-from-the-import-process',
                'https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.AnalysisFileCountLimitExceededError = AnalysisFileCountLimitExceededError;
/**
 * @class
 * @name AnalysisResultSizeLimitExceededError
 * @description This error occurs when the analysis target generates a result with a byte size that exceeds current system limits.
 *
 * To reduce the overall result size, use a `.snyk` file to ignore specified directories or files. Alternatively, use the Snyk CLI to analyze individual subdirectories separately.
 *
 * See more:
 * - [https://docs.snyk.io/scan-applications/start-scanning-using-the-cli-web-ui-or-api/snyk-code-and-your-repositories/excluding-directories-and-files-from-the-import-process](https://docs.snyk.io/scan-applications/start-scanning-using-the-cli-web-ui-or-api/snyk-code-and-your-repositories/excluding-directories-and-files-from-the-import-process)
 * - [https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli](https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli)
 * @summary Analysis result size limit exceeded
 * @category Code
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class AnalysisResultSizeLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Analysis result size limit exceeded',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-code-0002',
            status: 422,
            errorCode: 'SNYK-CODE-0002',
            level: 'error',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-applications/start-scanning-using-the-cli-web-ui-or-api/snyk-code-and-your-repositories/excluding-directories-and-files-from-the-import-process',
                'https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.AnalysisResultSizeLimitExceededError = AnalysisResultSizeLimitExceededError;
/**
 * @class
 * @name AnalysisTargetSizeLimitExceededError
 * @description This error occurs when the analysis target byte size exceeds current system limits.
 *
 * To reduce the overall result size, use a `.snyk` file to ignore specified directories or files. Alternatively, use the Snyk CLI to analyze individual subdirectories separately.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli](https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli)
 * @summary Analysis target size limit exceeded
 * @category Code
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class AnalysisTargetSizeLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Analysis target size limit exceeded',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-code-0003',
            status: 422,
            errorCode: 'SNYK-CODE-0003',
            level: 'error',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/using-snyk-code-from-the-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.AnalysisTargetSizeLimitExceededError = AnalysisTargetSizeLimitExceededError;
/**
 * @class
 * @name AnalysisFileNameLengthLimitExceededError
 * @description This error occurs when the analysis target has a file name length that exceeds 255 bytes.
 *
 * To be able to scan the analysis target, rename the file to a name that is 255 bytes or less.
 *
 * See more:
 * - [https://docs.snyk.io/scan-with-snyk/supported-languages-and-frameworks/introduction-to-snyk-supported-languages-and-frameworks#filename-length-limitation](https://docs.snyk.io/scan-with-snyk/supported-languages-and-frameworks/introduction-to-snyk-supported-languages-and-frameworks#filename-length-limitation)
 * @summary Analysis target includes a file with a name longer than 255 bytes
 * @category Code
 * @param {string} details                         the specific details that causes this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class AnalysisFileNameLengthLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Analysis target includes a file with a name longer than 255 bytes',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-code-0004',
            status: 422,
            errorCode: 'SNYK-CODE-0004',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-with-snyk/supported-languages-and-frameworks/introduction-to-snyk-supported-languages-and-frameworks#filename-length-limitation',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.AnalysisFileNameLengthLimitExceededError = AnalysisFileNameLengthLimitExceededError;
//# sourceMappingURL=Code-error-catalog.js.map